import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { useDubbingStore } from '@src/stores-v2/StoreContext';
import { MuxPauseIcon } from '@src/svgs/MuxPauseIcon';
import { MuxPlayIcon } from '@src/svgs/MuxPlayIcon';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import LanguageSamplePlayButton from './LanguageSamplePlayButton';
import { explainVoiceForRun } from './resolveVoiceForRun';
import { SamplePlayButton } from './samplePlayStyles';
import { useSpeakerAiVoices } from './useSpeakerAiVoices';
import { voiceSpeaksLanguage } from './voiceLanguageDefaults';

import type { DubPassagePlaybackState } from './useDubPassagePlayback';
import type { SpeakerWithColor } from '@src/services/SpeakerManagementService';
import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';
import type { Language } from '@src/types.ts/language';

export interface RedubSpeakerRowProps {
  speaker: SpeakerWithColor;
  /** The run's target language; null while resolving. */
  language: Language | null;
  /** Bumped when Manage AI Voices closes, so the row re-reads its voices. */
  reloadToken: number;
  /** Report the voice the run will use (undefined = none for this language). */
  onSelect: (speakerId: string, voiceId: string | undefined) => void;
  /**
   * PLAT-12078 — the row's one play sounds the dub's current take of the
   * selected lines (the host's `useDubPassagePlayback`). Without it, or once
   * the dub turns out to have no recording of the lines, the play is the
   * voice's language sample.
   */
  takePlay?: { state: DubPassagePlaybackState; toggle: () => void };
}

/**
 * PLAT-12022 — one speaker of a selection re-dub, read-only.
 *
 * The Redub Selection popover is for adjusting how a passage is delivered,
 * not for choosing who says it: the run reuses the voice the speaker already
 * has for the language, resolved exactly as the dub modal resolves it
 * (`explainVoiceForRun`: this session's pick, then the speaker's pin, then
 * the first of their voices that speaks the language). So the row shows the
 * voice as text with where it came from, and one play — no picker, which
 * would look changeable and is not (the popover says where voices are
 * changed, once, under the rows). The play sounds the line as the dub has it
 * now (PLAT-12078); only when the dub has no recording of the line does it
 * fall back to the voice's language sample.
 */
const RedubSpeakerRow = observer(
  ({
    speaker,
    language,
    reloadToken,
    onSelect,
    takePlay,
  }: RedubSpeakerRowProps) => {
    const dubbingStore = useDubbingStore();
    const { aiVoiceIds, languagePrefs, reload } = useSpeakerAiVoices(
      speaker.id,
    );

    useEffect(() => {
      if (reloadToken > 0) void reload();
    }, [reloadToken, reload]);

    const orderedVoices = useMemo(
      () =>
        aiVoiceIds
          .map(id => dubbingStore.voiceById(id))
          .filter((v): v is AiVoiceRecord => Boolean(v)),
      [aiVoiceIds, dubbingStore, dubbingStore.catalog],
    );
    const { voiceId, tier } = explainVoiceForRun({
      orderedVoices,
      languagePrefs,
      languageId: language?.id ?? null,
    });
    const voice = voiceId ? dubbingStore.voiceById(voiceId) : undefined;
    const speaks =
      !!voice && (!language || voiceSpeaksLanguage(voice, language));
    const effectiveVoiceId = speaks ? voiceId : undefined;

    useEffect(() => {
      onSelect(speaker.id, effectiveVoiceId);
    }, [effectiveVoiceId, speaker.id, onSelect]);

    const languageCaption = language?.caption ?? 'this language';
    const provenance =
      tier === 'pinned'
        ? `Default for ${languageCaption}`
        : tier === 'automatic'
          ? 'Auto-picked'
          : null;

    return (
      <Row data-testid={`redub-speaker-${speaker.id}`}>
        <SpeakerAvatar
          name={speaker.name}
          color={speaker.color}
          headshotUrl={speaker.headshots?.[0]?.url ?? null}
          size={28}
          initialsLength={2}
        />
        <span className="name">{speaker.name}</span>
        {speaks && voice ? (
          <>
            <span className="voice">
              {voice.name}
              {provenance && <span className="tier"> · {provenance}</span>}
            </span>
            {language &&
              (takePlay && takePlay.state !== 'unavailable' ? (
                // PLAT-12078 — the prominent weight: this play is the row's
                // only control and what the popover is for, hearing the
                // line as the dub has it now (and the new take once a
                // re-dub lands).
                <SamplePlayButton
                  type="button"
                  $size={28}
                  $prominent
                  $playing={takePlay.state === 'playing'}
                  onClick={takePlay.toggle}
                  disabled={takePlay.state === 'loading'}
                  aria-label={
                    takePlay.state === 'playing'
                      ? 'Pause the current take'
                      : 'Play the current take'
                  }
                  data-testid="redub-take-play"
                >
                  {takePlay.state === 'playing' ? (
                    <MuxPauseIcon
                      width="12"
                      height="12"
                      fillColor="currentColor"
                    />
                  ) : (
                    <MuxPlayIcon
                      width="12"
                      height="12"
                      fillColor="currentColor"
                    />
                  )}
                </SamplePlayButton>
              ) : (
                <LanguageSamplePlayButton
                  aiVoiceId={voice.id}
                  language={language.value}
                  prominent
                />
              ))}
          </>
        ) : (
          <span className="missing">No {languageCaption} AI Voice yet.</span>
        )}
      </Row>
    );
  },
);

export default RedubSpeakerRow;

const Row = styled.div`
  display: grid;
  grid-template-columns: 28px auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }

  .name {
    color: var(--foreground);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semi-bold);
    white-space: nowrap;
  }
  .voice {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-secondary);
    font-size: var(--text-xs);
  }
  .tier {
    color: var(--text-muted);
  }
  .missing {
    grid-column: 3 / span 2;
    color: var(--text-muted);
    font-size: var(--text-xs);
  }
`;
